import React from 'react';
import styled from 'styled-components';
// Import the images directly
import portraitImage from '../assets/images/_DSC7126.jpg';
import conductingImage from '../assets/images/_DSC7320.jpg';

// Main container using CSS Grid
const BioContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 3.5fr 1.5fr; /* Adjusted ratio for wider text column */
  grid-column-gap: 40px; /* Gap between columns */
  grid-row-gap: 20px; /* Gap between rows (e.g., title and content) */

  @media (max-width: 768px) {
    grid-template-columns: 1fr; /* Single column on smaller screens */
    grid-row-gap: 30px;
  }
`;

const PageTitle = styled.h1`
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  font-size: 1.65rem;
  margin: 0 0 5px 0; /* Adjust margin */
  color: #333;
  grid-column: 1 / -1; /* Make title span both columns */
`;

// Container specifically for the text content in the first column
const TextContent = styled.div`
  grid-column: 1 / 2; /* Place in first grid column */
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #444;

  p {
    font-size: inherit;
    line-height: inherit;
    margin-bottom: 1rem; 
    margin-top: 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 768px) {
     grid-column: 1 / -1; /* Span full width in single column layout */
     grid-row: 2 / 3; /* Ensure it comes after title */
  }
`;

// Container specifically for the images in the second column
const ImageContent = styled.div`
  grid-column: 2 / 3; /* Place in second grid column */
  display: flex;
  flex-direction: column;
  gap: 30px; /* Gap between images */

   @media (max-width: 768px) {
     grid-column: 1 / -1; /* Span full width */
     grid-row: 3 / 4; /* Ensure it comes after text */
     /* Optional: center images on mobile? */
     /* align-items: center; */ 
  }
`;

const BioImage = styled.img` /* Style the img tag directly */
    width: 100%;
  max-width: 300px; /* Optional: prevent images getting too large */
    border: 1px solid #ddd;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  display: block; 
  
  @media (max-width: 768px) {
     max-width: 80%; /* Example: slightly smaller on mobile */
     margin: 0 auto; /* Center image if container is centered */
  }
`;

// Use the imported images directly
const PORTRAIT_IMAGE = portraitImage;
const CONDUCTING_IMAGE = conductingImage;

const Bio: React.FC = () => {
  return (
    <BioContainer>
      <PageTitle>Biography</PageTitle>
      
      <TextContent>
          <p>
            Rory Wainwright Johnston is a conductor and composer from
            Bradford-on-Avon, near Bath. His musical education was fostered at
            Bath Abbey as head chorister and then tenor under the direction of
            Dr. Peter King. He read Music at the University of Manchester,
            graduating in 2016 with a first-class honours degree, specialising in
            conducting and composition. During this time he studied piano with
            Richard Casey, and conducting with Mark Heron (RNCM) and Justin
            Doyle (RIAS Kammerchor). After his Bachelors, he continued his
            compositional studies in Manchester on the Masters Composition
            course for two years under the tutelage of Camden Reeves and Nina
            Whiteman graduating with a Distinction in 2018. From April 2020 to
            July 2022 Rory studied for a Masters in Conducting with Justin Doyle
            at the Hochschule für Musik 'Hanns Eisler' in Berlin, graduating with a
            1.0.
          </p>
          <p>
            Rory is currently the assistant conductor of the Groot Omroepkoor
            (Netherlands Radio Choir), as well as the consortium vocal berlin and
            works as a regular deputy conductor for choirs in Berlin, such as the
            Philharmonischer Chor Berlin, Berliner Vokalkreis among others.
            Since 2022, Rory has developed a professional relationship with
            RIAS Kammerchor, working as a deputy and assistant conductor for
            Justin Doyle, including on their last two New Years Concerts in the
            Berlin Philharmonie and their subsequent recordings (2022 – Handel,
            Judas Maccabeaus; 2023 – Bach, Magnificat in Eb). Before moving to
            Berlin he worked with many choirs across the northwest of England,
            being Musical Director of Cantores Olicanae, Wigan Choral Society,
            The Brixi Singers, The Acapella Singers, and Manchester
            Renaissance Ensemble, he also worked as an Associate Conductor
            as part of the Hallé's Choral Team.
          </p>
          <p>
            Rory is in demand as a workshop leader with Early Music fora across
            the UK, and in July 2019 ran the Choral Workshop for the York Early
            Music Festival. In July 2022 he began a new relationship with Lacock
            Courses directing their Edinburgh Early Music Summer School, and
            has continued with courses in Gourdon, France (2023) and Ludlow,
            UK (2024).
          </p>
          <p>
          As a composer, his music has been performed and broadcast by a 
          variety of ensembles and artists from across the UK and Germany, 
          including RIAS Kammerchor, ORA Singers, BCMG, Kantos Chamber Choir, 
          Huddersfield Choral Society, ATLAS Vokalensemble and echo.
          </p>
          <p>
          Rory is passionate about composition education and to that end has 
          lead the 'Young Composers Apprentice Scheme' run by ORA Singers 
          across the UK as part of their Young Composer Competition, in which 
          40 students receive eight sessions of lessons, information, and 
          feedback about choral music, its history and how to write it.
          </p>
      </TextContent>

      <ImageContent>
          <BioImage src={PORTRAIT_IMAGE} alt="Rory Wainwright Johnston" />
          <BioImage src={CONDUCTING_IMAGE} alt="Rory Wainwright Johnston conducting" />
      </ImageContent>
    </BioContainer>
  );
};

export default Bio; 